import React, { Component } from 'react';
/**
 * ExplanationSteps — Shared numbered step list with LaTeX rendering.
 *
 * Two visual variants:
 * - 'card': Formal teacher-note style (used in ExplanationCard)
 * - 'conversational': Djay-style with connecting line (used in ExplanationContent)
 *
 * Extracted from ExplanationCard + ExplanationContent (R8-DUP1).
 */
import { motion } from 'framer-motion';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
// ── Types ────────────────────────────────────────────────────
interface ExplanationStepsProps {
  text?: string;
  steps?: string[];
  variant?: 'card' | 'conversational';
  /** Color accent for 'card' variant. Ignored for 'conversational'. */
  accent?: 'indigo' | 'amber';
}
// ── Component ────────────────────────────────────────────────
export function ExplanationSteps({
  text,
  steps,
  variant = 'card',
  accent = 'indigo'
}: ExplanationStepsProps) {
  const stepsToRender = steps || (text ? text.split('\n').filter(line => line.trim().length > 0) : []);
  // Single line — render as plain text
  if (stepsToRender.length <= 1 && text) {
    const textClass = variant === 'conversational' ? 'text-sm md:text-base leading-relaxed text-slate-700 font-medium' : 'text-lg leading-relaxed text-slate-700';
    return <div className={textClass} data-id="element-8028">
        <LatexRenderer content={text} data-id="element-8029" />
      </div>;
  }
  // Multi-step — numbered list with staggered animation
  if (variant === 'conversational') {
    return <div className="space-y-3 relative" data-id="element-8030">
        <div className="absolute left-3 top-4 bottom-4 w-0.5 bg-orange-300 rounded-full" data-id="element-8031" />
        {stepsToRender.map((step, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.08 * idx,
        duration: 0.3,
        ease: 'easeOut'
      }} className="flex gap-3 items-start relative" data-id="element-8032">
            <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-black text-sm bg-orange-500 text-white shadow-md shadow-orange-200/50 z-10" data-id="element-8033">
              {idx + 1}
            </div>
            <div className="flex-1 text-sm md:text-base leading-relaxed pt-0.5 text-slate-700 font-medium bg-white/50 rounded-xl px-3 py-2 border border-slate-100" data-id="element-8034">
              <LatexRenderer content={step} data-id="element-8035" />
            </div>
          </motion.div>)}
      </div>;
  }
  // 'card' variant
  const stepBg = accent === 'amber' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-indigo-50 text-indigo-600 border-indigo-100';
  return <div className="space-y-4" data-id="element-8036">
      {stepsToRender.map((step, idx) => <motion.div key={idx} initial={{
      opacity: 0,
      x: -8
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.05 * idx,
      duration: 0.3
    }} className="flex gap-4 items-start" data-id="element-8037">
          <div className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center font-black text-sm border shadow-sm mt-0.5 ${stepBg}`} data-id="element-8038">
            {idx + 1}
          </div>
          <div className="flex-1 text-lg leading-relaxed pt-0.5 text-slate-700" data-id="element-8039">
            <LatexRenderer content={step} data-id="element-8040" />
          </div>
        </motion.div>)}
    </div>;
}